import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useTimer, formatDuration } from "@/contexts/TimerContext";
import { toast } from "sonner";
import {
  Clock, Play, Pause, Square, CheckCircle, ListTodo, AlertTriangle, AlertCircle, FileText, Plus, Trash2, Printer, ChevronRight, FileCheck, Users
} from "lucide-react";
import { TASK_STATUS_LABELS, statusBadgeClass, priorityBadgeClass, hasPerm } from "@/lib/constants";

export default function WorkReportsPage() {
  const { user: me } = useAuth();
  const { timer: activeTimer, elapsed: activeElapsed, start: tStart, pause: tPause, stop: tStop } = useTimer();
  const canCreate = hasPerm(me, "work_report.create");

  // Tab State
  const [activeTab, setActiveTab] = useState("timer");

  // General State
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [unassignedTasks, setUnassignedTasks] = useState([]);

  // Selected Task in Timer tab
  const [selectedTask, setSelectedTask] = useState(null);

  // Standup Form State
  const [standupForm, setStandupForm] = useState({
    team_id: "",
    completed_tasks: [],
    completed_additional: [],
    planned_tasks: [],
    planned_additional: [],
    backlog_tasks: [],
    blockers: "",
    escalations: "",
    notes: "",
    date: new Date().toISOString().split("T")[0]
  });
  const [newCompletedAdditional, setNewCompletedAdditional] = useState("");
  const [newPlannedAdditional, setNewPlannedAdditional] = useState("");
  const [myHistory, setMyHistory] = useState([]);
  const [selectedHistoryReport, setSelectedHistoryReport] = useState(null);
  const [errors, setErrors] = useState({});

  // Team Aggregation State
  const [aggregateTeamId, setAggregateTeamId] = useState("");
  const [aggregateDate, setAggregateDate] = useState(new Date().toISOString().split("T")[0]);
  const [teamReportData, setTeamReportData] = useState(null);
  const [loadingTeamReport, setLoadingTeamReport] = useState(false);

  // User Work Report Tab States (within WorkReportsPage)
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [userFilterType, setUserFilterType] = useState("all");
  const [userFromDate, setUserFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [userToDate, setUserToDate] = useState(new Date().toISOString().split("T")[0]);
  const [userTeamId, setUserTeamId] = useState("");
  const [userProjectId, setUserProjectId] = useState("");
  const [userWorkReport, setUserWorkReport] = useState(null);

  // Load Initial Data
  const loadData = useCallback(async () => {
    try {
      const [projRes, teamRes, taskRes, userRes] = await Promise.all([
        api.get("/projects").catch(() => ({ data: { items: [] } })),
        api.get("/teams/all").catch(() => ({ data: [] })),
        api.get("/tasks?page_size=1000").catch(() => ({ data: { items: [] } })),
        api.get("/users/all").catch(() => ({ data: [] }))
      ]);

      const projs = projRes.data.items || [];
      const tms = teamRes.data || [];
      const tasks = taskRes.data.items || [];
      const ulist = userRes.data || [];

      setProjects(projs);
      setTeams(tms);
      setAllTasks(tasks);
      setAllUsers(ulist);

      if (ulist.length > 0 && !selectedUser) {
        setSelectedUser(ulist[0].id);
      }
      if (tms.length > 0 && !userTeamId) {
        setUserTeamId(tms[0].id);
      }
      if (projs.length > 0 && !userProjectId) {
        setUserProjectId(projs[0].id);
      }

      // Filter tasks assigned to me
      const assigned = tasks.filter(t => t.assignee_id === me.id);
      setMyTasks(assigned);

      // Filter unassigned tasks in projects I'm member/lead of
      const projIds = projs.map(p => p.id);
      const unassigned = tasks.filter(t => !t.assignee_id && projIds.includes(t.project_id));
      setUnassignedTasks(unassigned);

      if (assigned.length > 0 && !selectedTask) {
        setSelectedTask(assigned[0]);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [me.id, selectedTask]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/work-reports?user_id=${me.id}`);
      setMyHistory(data || []);
    } catch {}
  }, [me.id]);

  useEffect(() => {
    loadData();
    loadHistory();
  }, [loadData, loadHistory]);

  const [loadingUserReport, setLoadingUserReport] = useState(false);

  const fetchUserWorkReport = useCallback(async () => {
    if (!selectedUser) return;
    setLoadingUserReport(true);
    try {
      let url = `/reports/user/${selectedUser}/work-report?filter_type=${userFilterType}&from_date=${userFromDate}&to_date=${userToDate}`;
      if (userFilterType === "team" && userTeamId) {
        url += `&team_id=${userTeamId}`;
      } else if (userFilterType === "project" && userProjectId) {
        url += `&project_id=${userProjectId}`;
      }
      const { data } = await api.get(url);
      setUserWorkReport(data);
    } catch (e) {
      toast.error("Failed to fetch report: " + formatApiError(e));
    } finally {
      setLoadingUserReport(false);
    }
  }, [selectedUser, userFilterType, userFromDate, userToDate, userTeamId, userProjectId]);

  useEffect(() => {
    if (activeTab === "user_reports") {
      fetchUserWorkReport();
    }
  }, [activeTab, fetchUserWorkReport]);

  useEffect(() => {
    const handleTimerStopped = () => {
      loadData();
      loadHistory();
    };
    window.addEventListener("timer-stopped", handleTimerStopped);
    return () => {
      window.removeEventListener("timer-stopped", handleTimerStopped);
    };
  }, [loadData, loadHistory]);

  const syncFormWithHistory = useCallback((date, teamId, historyList = myHistory) => {
    const existing = historyList.find(
      (h) => h.date === date && (h.team_id || "") === (teamId || "")
    );
    
    (async () => {
      try {
        const { data: draftData } = await api.get(`/work-reports/draft?team_id=${teamId || ""}`);
        
        if (existing) {
          setStandupForm(prev => ({
            ...prev,
            completed_tasks: draftData.completed_tasks || [],
            planned_tasks: draftData.planned_tasks || [],
            backlog_tasks: draftData.backlog_tasks || [],
            // Deduplicate and merge system actions with user's manual completed actions
            completed_additional: Array.from(new Set([
              ...(draftData.completed_additional || []),
              ...(existing.completed_additional || [])
            ])),
            planned_additional: existing.planned_additional || [],
            blockers: existing.blockers || "",
            escalations: existing.escalations || "",
            notes: existing.notes || "",
          }));
        } else {
          setStandupForm(prev => ({
            ...prev,
            completed_tasks: draftData.completed_tasks || [],
            planned_tasks: draftData.planned_tasks || [],
            backlog_tasks: draftData.backlog_tasks || [],
            completed_additional: draftData.completed_additional || [],
            planned_additional: [],
            blockers: "",
            escalations: "",
            notes: "",
          }));
        }
      } catch {}
    })();
  }, [myHistory]);

  useEffect(() => {
    if (activeTab === "standup") {
      syncFormWithHistory(standupForm.date, standupForm.team_id);
    }
  }, [activeTab, syncFormWithHistory]);

  // Self Assign (Pick) Task
  const pickTask = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}`, { assignee_id: me.id });
      toast.success("Task assigned to you");
      await loadData();
    } catch (e) {
      toast.error("Failed to assign task: " + formatApiError(e));
    }
  };

  // Standup List Additions
  const addCompletedAdditional = () => {
    if (!newCompletedAdditional.trim()) return;
    setStandupForm(prev => ({
      ...prev,
      completed_additional: [...prev.completed_additional, newCompletedAdditional.trim()]
    }));
    setNewCompletedAdditional("");
    setErrors(prev => ({ ...prev, completed: "" }));
  };

  const removeCompletedAdditional = (idx) => {
    setStandupForm(prev => ({
      ...prev,
      completed_additional: prev.completed_additional.filter((_, i) => i !== idx)
    }));
  };

  const addPlannedAdditional = () => {
    if (!newPlannedAdditional.trim()) return;
    setStandupForm(prev => ({
      ...prev,
      planned_additional: [...prev.planned_additional, newPlannedAdditional.trim()]
    }));
    setNewPlannedAdditional("");
    setErrors(prev => ({ ...prev, planned: "" }));
  };

  const removePlannedAdditional = (idx) => {
    setStandupForm(prev => ({
      ...prev,
      planned_additional: prev.planned_additional.filter((_, i) => i !== idx)
    }));
  };

  // Submit Standup
  const submitStandup = async (e) => {
    e.preventDefault();
    
    // Custom inline validations
    const errs = {};
    if (!standupForm.date) {
      errs.date = "Date is required.";
    }
    if (standupForm.completed_tasks.length === 0 && standupForm.completed_additional.length === 0) {
      errs.completed = "Please log at least one completed task or manual completed update.";
    }
    if (standupForm.planned_tasks.length === 0 && standupForm.planned_additional.length === 0) {
      errs.planned = "Please log at least one planned task or manual planned update.";
    }
    
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast.error("Please correct the errors in the standup form.");
      return;
    }

    if (!standupForm.team_id) {
      const confirmGeneric = window.confirm("Are you sure you want to submit this report under 'No Team / Generic'?");
      if (!confirmGeneric) {
        return;
      }
    }

    try {
      await api.post("/work-reports", standupForm);
      toast.success("Daily Standup Report submitted/updated successfully");
      
      const historyRes = await api.get(`/work-reports?user_id=${me.id}`);
      const updatedHistory = historyRes.data || [];
      setMyHistory(updatedHistory);
      
      // Keep form synchronized with the newly saved/updated report in history
      syncFormWithHistory(standupForm.date, standupForm.team_id, updatedHistory);
      setErrors({});
    } catch (e) {
      toast.error("Failed to submit report: " + formatApiError(e));
    }
  };

  // Generate Aggregated Team Report
  const generateTeamReport = async () => {
    if (!aggregateTeamId) {
      toast.error("Please select a team");
      return;
    }
    setLoadingTeamReport(true);
    try {
      const { data } = await api.get(`/work-reports/team/${aggregateTeamId}?date=${aggregateDate}`);
      setTeamReportData(data);
    } catch (e) {
      toast.error("Failed to fetch team report: " + formatApiError(e));
    } finally {
      setLoadingTeamReport(false);
    }
  };

  // Helper formatting actual hrs
  const getSpentHours = (min) => roundFloat(min / 60.0, 2);
  const roundFloat = (val, dec) => Math.round(val * Math.pow(10, dec)) / Math.pow(10, dec);

  // Time Comparison Badge
  const getDiffBadge = (est, act) => {
    const diff = act - est;
    if (diff > 0) {
      return <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">+{roundFloat(diff, 2)}h (Over)</Badge>;
    } else if (diff < 0) {
      return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{roundFloat(diff, 2)}h (Under)</Badge>;
    }
    return <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">On Target</Badge>;
  };

  const isLead = me.role === "TEAM_LEAD";
  const isAdmin = ["SUPER_ADMIN", "ADMIN", "PROJECT_MANAGER"].includes(me.role);

  return (
    <div className="space-y-6" data-testid="work-reports-page">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Work Report</h1>
          <p className="text-sm text-muted-foreground mt-1">Log time, check progress against estimates, and submit standups.</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full max-w-[650px] ${(isLead || isAdmin) ? 'grid-cols-4' : 'grid-cols-2'}`}>
          <TabsTrigger value="timer" className="flex items-center gap-2">
            <Clock className="w-4 h-4" /> Timer & Tasks
          </TabsTrigger>
          <TabsTrigger value="standup" className="flex items-center gap-2">
            <FileText className="w-4 h-4" /> Daily Standup
          </TabsTrigger>
          {(isLead || isAdmin) && (
            <TabsTrigger value="team" className="flex items-center gap-2">
              <Printer className="w-4 h-4" /> Team Standups
            </TabsTrigger>
          )}
          {(isLead || isAdmin) && (
            <TabsTrigger value="user_reports" className="flex items-center gap-2">
              <Users className="w-4 h-4" /> User Reports
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab 1: Timer & Tasks */}
        <TabsContent value="timer" className="space-y-6 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Task selection pane */}
            <div className="lg:col-span-1 space-y-4">
              <Card className="p-4 space-y-3">
                <div className="flex items-center gap-2 border-b border-border pb-2">
                  <ListTodo className="w-4 h-4 text-primary" />
                  <span className="font-semibold text-sm uppercase tracking-wider">My Assigned Tasks</span>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {myTasks.map(t => (
                    <div
                      key={t.id}
                      onClick={() => setSelectedTask(t)}
                      className={`p-3 rounded-md border text-sm cursor-pointer transition hover:bg-muted/40 ${
                        selectedTask?.id === t.id ? "border-primary bg-primary/5" : "border-border"
                      }`}
                    >
                      <div className="font-semibold line-clamp-1">{t.title}</div>
                      <div className="flex justify-between items-center mt-2">
                        <Badge variant="outline" className={statusBadgeClass(t.status)}>{TASK_STATUS_LABELS[t.status]}</Badge>
                        <span className="text-[11px] text-muted-foreground">Est: {t.estimated_hours || 0}h</span>
                      </div>
                    </div>
                  ))}
                  {myTasks.length === 0 && (
                    <div className="text-center py-6 text-xs text-muted-foreground">No tasks assigned to you.</div>
                  )}
                </div>
              </Card>

              {/* Unassigned Tasks Section */}
              {myTasks.length === 0 && (
                <Card className="p-4 space-y-3">
                  <div className="flex items-center gap-2 border-b border-border pb-2 text-amber-600">
                    <AlertCircle className="w-4 h-4" />
                    <span className="font-semibold text-sm uppercase tracking-wider">Pick Unassigned Task</span>
                  </div>
                  <div className="space-y-2 max-h-[250px] overflow-y-auto pr-1">
                    {unassignedTasks.map(t => (
                      <div key={t.id} className="p-3 rounded-md border border-border text-sm flex flex-col gap-2">
                        <div className="font-semibold line-clamp-1">{t.title}</div>
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] text-muted-foreground">Est: {t.estimated_hours || 0}h</span>
                          <Button size="xs" disabled={!canCreate} onClick={() => pickTask(t.id)}>Pick Task</Button>
                        </div>
                      </div>
                    ))}
                    {unassignedTasks.length === 0 && (
                      <div className="text-center py-6 text-xs text-muted-foreground">No unassigned tasks found.</div>
                    )}
                  </div>
                </Card>
              )}
            </div>

            {/* Timer Console & Details */}
            <div className="lg:col-span-2 space-y-6">
              {selectedTask ? (
                <Card className="p-6 space-y-5">
                  <div>
                    <span className="text-xs text-muted-foreground font-mono">Task ID: #{selectedTask.id.slice(-6)}</span>
                    <h2 className="text-xl font-bold mt-1 text-card-foreground">{selectedTask.title}</h2>
                    <p className="text-sm text-muted-foreground mt-2 bg-muted/20 p-3 rounded-md whitespace-pre-wrap">
                      {selectedTask.description || "No description provided."}
                    </p>
                  </div>

                  {/* Integrated Timer Console */}
                  {(() => {
                    const isMine = activeTimer && activeTimer.task_id === selectedTask.id;
                    const running = isMine && activeTimer.status === "RUNNING";
                    const paused = isMine && activeTimer.status === "PAUSED";
                    const display = isMine ? formatDuration(activeElapsed) : formatDuration(0);
                    return (
                      <div className="p-5 rounded-lg border border-primary/20 bg-primary/5 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <Clock className={`w-8 h-8 ${running ? "text-primary animate-pulse" : "text-muted-foreground"}`} />
                          <div>
                            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Active Timer</div>
                            <div className="font-mono text-3xl font-bold text-primary">{display}</div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {!isMine && (
                            <Button onClick={() => tStart(selectedTask.id)} disabled={!canCreate} className="w-28">
                              <Play className="w-4 h-4 mr-1.5" /> Start
                            </Button>
                          )}
                          {running && (
                            <Button variant="outline" onClick={() => tPause(selectedTask.id)} disabled={!canCreate} className="w-28">
                              <Pause className="w-4 h-4 mr-1.5" /> Pause
                            </Button>
                          )}
                          {paused && (
                            <Button onClick={() => tStart(selectedTask.id)} disabled={!canCreate} className="w-28">
                              <Play className="w-4 h-4 mr-1.5" /> Resume
                            </Button>
                          )}
                          {isMine && (
                            <Button variant="outline" onClick={async () => { await tStop(selectedTask.id); loadData(); }} disabled={!canCreate} className="w-28">
                              <Square className="w-4 h-4 mr-1.5" /> Stop
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Estimation Comparison metrics */}
                  <div className="border-t border-border pt-4">
                    <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Time Tracker Comparison</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="border border-border rounded-md p-3">
                        <div className="text-xs text-muted-foreground">Estimated Time</div>
                        <div className="text-lg font-semibold mt-1">{selectedTask.estimated_hours || 0}h</div>
                      </div>
                      <div className="border border-border rounded-md p-3">
                        <div className="text-xs text-muted-foreground">Actual Spent Time</div>
                        <div className="text-lg font-semibold mt-1 text-primary">{getSpentHours(selectedTask.total_minutes_tracked || 0)}h</div>
                      </div>
                      <div className="border border-border rounded-md p-3">
                        <div className="text-xs text-muted-foreground">Minutes Logged</div>
                        <div className="text-lg font-semibold mt-1">{Math.round(selectedTask.total_minutes_tracked || 0)} mins</div>
                      </div>
                      <div className="border border-border rounded-md p-3 flex flex-col justify-center">
                        <div className="text-xs text-muted-foreground">Status</div>
                        <div className="mt-1">{getDiffBadge(selectedTask.estimated_hours || 0, getSpentHours(selectedTask.total_minutes_tracked || 0))}</div>
                      </div>
                    </div>
                  </div>
                </Card>
              ) : (
                <div className="h-full flex items-center justify-center p-12 text-muted-foreground border rounded-lg border-dashed">
                  Select an assigned task from the left panel to begin work.
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Tab 2: Daily Standup creator */}
        <TabsContent value="standup" className="space-y-6 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Form Column */}
            <Card className="lg:col-span-2 p-6 space-y-6">
              <div className="border-b border-border pb-3">
                <h2 className="text-xl font-bold">New Daily Standup</h2>
                <p className="text-xs text-muted-foreground mt-1">Submit your standup report. Completed and Planned tasks are generated by the system.</p>
              </div>

               <form onSubmit={submitStandup} className="space-y-4">
                <fieldset disabled={!canCreate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={standupForm.date} onChange={(e) => { const newDate = e.target.value; setStandupForm(prev => ({ ...prev, date: newDate })); syncFormWithHistory(newDate, standupForm.team_id); setErrors(prev => ({ ...prev, date: "" })); }} required />
                      {errors.date && <span className="text-xs text-red-500 block mt-1">{errors.date}</span>}
                    </div>
                    <div>
                      <Label>Select Team</Label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background mt-2"
                        value={standupForm.team_id}
                        onChange={(e) => { const newTeam = e.target.value; setStandupForm(prev => ({ ...prev, team_id: newTeam })); syncFormWithHistory(standupForm.date, newTeam); }}
                      >
                        <option value="">No Team / Generic</option>
                        {teams.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* System completed tasks */}
                  <div>
                    <Label className="font-semibold text-primary">Completed Work (System Generated)</Label>
                    <div className="mt-2 space-y-2 border rounded-md p-3 max-h-40 overflow-y-auto bg-muted/10">
                      {standupForm.completed_tasks.map((t, idx) => (
                        <div key={idx} className="flex justify-between items-center text-xs p-1 border-b last:border-0 border-border">
                          <span className="font-semibold line-clamp-1">{t.title}</span>
                          <span className="text-muted-foreground font-mono">Est: {t.estimated_hours}h, Spent: {t.actual_hours}h</span>
                        </div>
                      ))}
                      {standupForm.completed_tasks.length === 0 && (
                        <div className="text-xs text-muted-foreground">No tasks completed in the last 24 hours.</div>
                      )}
                    </div>
                  </div>

                  {/* Manual additions for completed work */}
                  <div>
                    <Label>Additional Completed Work (Meetings, Bug Fixes, etc.)</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        value={newCompletedAdditional}
                        onChange={(e) => setNewCompletedAdditional(e.target.value)}
                        placeholder="e.g. Daily Standup meeting & code reviews"
                      />
                      <Button type="button" onClick={addCompletedAdditional} variant="outline" size="sm">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="mt-2 space-y-1">
                      {standupForm.completed_additional.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-xs p-2 bg-muted/30 rounded border">
                          <span>{item}</span>
                          <Trash2 className="w-3.5 h-3.5 text-rose-500 cursor-pointer hover:scale-110 transition" onClick={() => removeCompletedAdditional(idx)} />
                        </div>
                      ))}
                    </div>
                    {errors.completed && <span className="text-xs text-red-500 block mt-1">{errors.completed}</span>}
                  </div>

                  {/* System planned tasks */}
                  <div>
                    <Label className="font-semibold text-primary">Planned Work (System Generated)</Label>
                    <div className="mt-2 space-y-2 border rounded-md p-3 max-h-40 overflow-y-auto bg-muted/10">
                      {standupForm.planned_tasks.map((t, idx) => (
                        <div key={idx} className="flex justify-between items-center text-xs p-1 border-b last:border-0 border-border">
                          <span className="font-semibold line-clamp-1">
                            {t.title} {t.pending_days > 0 && <span className="text-rose-500 font-normal">({t.pending_days}d pending)</span>}
                          </span>
                          <span className="text-muted-foreground font-mono">Est: {t.estimated_hours}h</span>
                        </div>
                      ))}
                      {standupForm.planned_tasks.length === 0 && (
                        <div className="text-xs text-muted-foreground">No pending/in-progress tasks assigned.</div>
                      )}
                    </div>
                  </div>

                  {/* Manual additions for planned work */}
                  <div>
                    <Label>Additional Planned Work</Label>
                    <div className="flex gap-2 mt-2">
                      <Input
                        value={newPlannedAdditional}
                        onChange={(e) => setNewPlannedAdditional(e.target.value)}
                        placeholder="e.g. Plan API routes for report generation"
                      />
                      <Button type="button" onClick={addPlannedAdditional} variant="outline" size="sm">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="mt-2 space-y-1">
                      {standupForm.planned_additional.map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center text-xs p-2 bg-muted/30 rounded border">
                          <span>{item}</span>
                          <Trash2 className="w-3.5 h-3.5 text-rose-500 cursor-pointer hover:scale-110 transition" onClick={() => removePlannedAdditional(idx)} />
                        </div>
                      ))}
                    </div>
                    {errors.planned && <span className="text-xs text-red-500 block mt-1">{errors.planned}</span>}
                  </div>

                  {/* System backlog tasks */}
                  <div>
                    <Label className="font-semibold text-primary">Backlog Tasks (System Generated)</Label>
                    <div className="mt-2 space-y-2 border rounded-md p-3 max-h-40 overflow-y-auto bg-muted/10">
                      {standupForm.backlog_tasks?.map((t, idx) => (
                        <div key={idx} className="flex justify-between items-center text-xs p-1 border-b last:border-0 border-border">
                          <span className="font-semibold line-clamp-1">
                            {t.title} {t.pending_days > 0 && <span className="text-rose-500 font-normal">({t.pending_days}d pending)</span>}
                          </span>
                          <span className="text-muted-foreground font-mono">Est: {t.estimated_hours}h</span>
                        </div>
                      ))}
                      {(!standupForm.backlog_tasks || standupForm.backlog_tasks.length === 0) && (
                        <div className="text-xs text-muted-foreground">No backlog tasks assigned.</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>Blockers / Issues</Label>
                    <Textarea
                      className="mt-2 text-sm"
                      value={standupForm.blockers}
                      onChange={(e) => setStandupForm({ ...standupForm, blockers: e.target.value })}
                      placeholder="Describe any issues holding you back"
                    />
                  </div>

                  <div>
                    <Label>Escalations</Label>
                    <Textarea
                      className="mt-2 text-sm"
                      value={standupForm.escalations}
                      onChange={(e) => setStandupForm({ ...standupForm, escalations: e.target.value })}
                      placeholder="High priority escalations if any"
                    />
                  </div>

                  <div>
                    <Label>Additional Notes</Label>
                    <Textarea
                      className="mt-2 text-sm"
                      value={standupForm.notes}
                      onChange={(e) => setStandupForm({ ...standupForm, notes: e.target.value })}
                      placeholder="Notes..."
                    />
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button type="submit">Submit Standup</Button>
                  </div>
                </fieldset>
              </form>
            </Card>

            {/* History Column */}
            <Card className="lg:col-span-1 p-6 space-y-4">
              <div className="border-b border-border pb-3">
                <h3 className="font-semibold text-sm uppercase tracking-wider">My Submitted Standups</h3>
              </div>
              <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                {myHistory.map(h => (
                  <div
                    key={h.id}
                    onClick={() => setSelectedHistoryReport(h)}
                    className="p-3 rounded-md border border-border hover:bg-muted/40 cursor-pointer transition flex items-center justify-between text-xs"
                  >
                    <div>
                      <div className="font-semibold text-primary">
                        {h.date} {h.team_name ? `(${h.team_name})` : "(Individual)"}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Completed: {h.completed_tasks.length + h.completed_additional.length} items
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                ))}
                {myHistory.length === 0 && (
                  <div className="text-center py-10 text-xs text-muted-foreground">You haven't submitted any standups yet.</div>
                )}
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Tab 3: Team Standups (Aggregated Print Report) */}
        {(isLead || isAdmin) && (
          <>
            <TabsContent value="team" className="space-y-6 mt-4">
            <Card className="p-6 space-y-5">
              <div className="border-b border-border pb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Team Standups</h2>
                  <p className="text-xs text-muted-foreground mt-1">Aggregate standup updates submitted by all team members on a specific date.</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 items-end">
                <div className="w-64">
                  <Label>Select Team</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background mt-2"
                    value={aggregateTeamId}
                    onChange={(e) => setAggregateTeamId(e.target.value)}
                  >
                    <option value="">-- Choose Team --</option>
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Select Date</Label>
                  <Input type="date" value={aggregateDate} onChange={(e) => setAggregateDate(e.target.value)} className="w-48 mt-2" />
                </div>
                <Button onClick={generateTeamReport} disabled={loadingTeamReport}>
                  {loadingTeamReport ? "Generating…" : "Generate standup report"}
                </Button>
                {teamReportData && (
                  <Button variant="outline" onClick={() => window.print()}>
                    <Printer className="w-4 h-4 mr-1.5" /> Print Report
                  </Button>
                )}
              </div>

              {/* Renders the Print Layout PDF View */}
              {teamReportData ? (
                <div className="border border-border rounded-md p-6 bg-white text-slate-800 space-y-6 print:border-0 print:p-0" id="standup-print-area">
                  <div className="text-center border-b-2 border-slate-900 pb-4">
                    <h1 className="text-2xl font-bold text-orange-600 tracking-wider">DAILY STANDUP REPORT</h1>
                    <div className="text-[11px] text-slate-500 font-medium italic mt-1">Standup Time: 9:45 AM | Submission Deadline: 10:30 AM</div>
                  </div>

                  <div className="grid grid-cols-2 border border-slate-300 text-xs bg-slate-50/50">
                    <div className="grid grid-cols-3 border-r border-slate-300 p-2 items-center">
                      <span className="font-bold col-span-1">Team</span>
                      <span className="col-span-2 border-l border-slate-200 pl-2 font-mono uppercase">{teamReportData.team_name}</span>
                    </div>
                    <div className="grid grid-cols-3 p-2 items-center">
                      <span className="font-bold col-span-1">Date</span>
                      <span className="col-span-2 border-l border-slate-200 pl-2 font-mono">{teamReportData.date}</span>
                    </div>
                    <div className="grid grid-cols-3 border-t border-r border-slate-300 p-2 items-center col-span-1">
                      <span className="font-bold col-span-1">Attendees</span>
                      <span className="col-span-2 border-l border-slate-200 pl-2">{teamReportData.attendees.join(", ") || "—"}</span>
                    </div>
                    <div className="grid grid-cols-3 border-t border-slate-300 p-2 items-center col-span-1">
                      <span className="font-bold col-span-1">Missed Meeting</span>
                      <span className="col-span-2 border-l border-slate-200 pl-2 text-rose-600">{teamReportData.missed_meeting.join(", ") || "None"}</span>
                    </div>
                  </div>

                  {/* Section 1: Work Completed Yesterday */}
                  <div className="space-y-2">
                    <h3 className="font-bold text-sm text-slate-900 border-b border-slate-300 pb-1">1. Work Completed (Yesterday)</h3>
                    <div className="border border-slate-300 rounded overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 text-slate-700 font-bold border-b border-slate-300">
                          <tr>
                            <th className="px-3 py-2 text-left w-1/4 border-r border-slate-300">Member Name</th>
                            <th className="px-3 py-2 text-left">Task / Update</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamReportData.completed_yesterday.map((row, idx) => (
                            <tr key={idx} className="border-t border-slate-300">
                              <td className="px-3 py-2 font-semibold bg-slate-50/30 border-r border-slate-300 align-top">{row.member_name}</td>
                              <td className="px-3 py-2 align-top">
                                <ul className="list-decimal pl-4 space-y-1">
                                  {row.updates.map((up, i) => <li key={i}>{up}</li>)}
                                  {row.updates.length === 0 && <span className="text-slate-400 italic">No updates reported</span>}
                                </ul>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Section 2: Planned Work Today */}
                  <div className="space-y-2">
                    <h3 className="font-bold text-sm text-slate-900 border-b border-slate-300 pb-1">2. Planned Work (Today)</h3>
                    <div className="border border-slate-300 rounded overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 text-slate-700 font-bold border-b border-slate-300">
                          <tr>
                            <th className="px-3 py-2 text-left w-1/4 border-r border-slate-300">Member Name</th>
                            <th className="px-3 py-2 text-left">Task / Update</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamReportData.planned_today.map((row, idx) => (
                            <tr key={idx} className="border-t border-slate-300">
                              <td className="px-3 py-2 font-semibold bg-slate-50/30 border-r border-slate-300 align-top">{row.member_name}</td>
                              <td className="px-3 py-2 align-top">
                                <ul className="list-decimal pl-4 space-y-1">
                                  {row.updates.map((up, i) => <li key={i}>{up}</li>)}
                                  {row.updates.length === 0 && <span className="text-slate-400 italic">No plans reported</span>}
                                </ul>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Section 3: Backlog Tasks (Pending) */}
                  <div className="space-y-2">
                    <h3 className="font-bold text-sm text-slate-900 border-b border-slate-300 pb-1">3. Backlog Tasks (Pending)</h3>
                    <div className="border border-slate-300 rounded overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 text-slate-700 font-bold border-b border-slate-300">
                          <tr>
                            <th className="px-3 py-2 text-left w-1/4 border-r border-slate-300">Member Name</th>
                            <th className="px-3 py-2 text-left">Task / Update</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamReportData.backlog_tasks?.map((row, idx) => (
                            <tr key={idx} className="border-t border-slate-300">
                              <td className="px-3 py-2 font-semibold bg-slate-50/30 border-r border-slate-300 align-top">{row.member_name}</td>
                              <td className="px-3 py-2 align-top">
                                <ul className="list-decimal pl-4 space-y-1">
                                  {row.updates.map((up, i) => <li key={i}>{up}</li>)}
                                  {row.updates.length === 0 && <span className="text-slate-400 italic">No backlog reported</span>}
                                </ul>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Section 4: Blockers */}
                  <div className="space-y-2">
                    <h3 className="font-bold text-sm text-slate-900 border-b border-slate-300 pb-1">4. Blockers / Issues</h3>
                    <div className="border border-slate-300 rounded overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 text-slate-700 font-bold border-b border-slate-300">
                          <tr>
                            <th className="px-3 py-2 text-left w-1/4 border-r border-slate-300">Member Name</th>
                            <th className="px-3 py-2 text-left">Blocker Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamReportData.blockers.map((b, idx) => (
                            <tr key={idx} className="border-t border-slate-300">
                              <td className="px-3 py-2 font-semibold bg-slate-50/30 border-r border-slate-300 align-top">{b.member_name}</td>
                              <td className="px-3 py-2 align-top text-rose-600 font-medium">{b.text}</td>
                            </tr>
                          ))}
                          {teamReportData.blockers.length === 0 && (
                            <tr>
                              <td colSpan={2} className="px-3 py-4 text-center text-slate-400 italic">No blockers reported for today.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Section 5: Escalations */}
                  {teamReportData.escalations.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="font-bold text-sm text-slate-900 border-b border-slate-300 pb-1">5. Escalations</h3>
                      <ul className="list-disc pl-5 text-xs text-rose-700 space-y-1 font-semibold">
                        {teamReportData.escalations.map((e, idx) => (
                          <li key={idx}><strong>{e.member_name}:</strong> {e.text}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-10 text-xs text-muted-foreground border rounded-lg border-dashed">
                  Select a team and date above to load the Daily Standup report.
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="user_reports" className="space-y-4">
            <Card className="p-6 space-y-6">
              <div>
                <h3 className="font-semibold text-lg">User Work Reports</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Filter work reports, estimated hours, and activity history for any user.</p>
              </div>

              <div className="flex flex-wrap items-end gap-3 bg-muted/10 p-4 rounded-lg border">
                <div className="space-y-1.5 flex-1 min-w-[200px]">
                  <Label className="text-xs">Select User</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1.5"
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                  >
                    {allUsers.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5 w-44">
                  <Label className="text-xs">Filter Type</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1.5"
                    value={userFilterType}
                    onChange={(e) => setUserFilterType(e.target.value)}
                  >
                    <option value="all">All Tasks</option>
                    <option value="team">By Team</option>
                    <option value="project">By Project</option>
                  </select>
                </div>

                {userFilterType === "team" && (
                  <div className="space-y-1.5 w-48">
                    <Label className="text-xs">Select Team</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1.5"
                      value={userTeamId}
                      onChange={(e) => setUserTeamId(e.target.value)}
                    >
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {userFilterType === "project" && (
                  <div className="space-y-1.5 w-48">
                    <Label className="text-xs">Select Project</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1.5"
                      value={userProjectId}
                      onChange={(e) => setUserProjectId(e.target.value)}
                    >
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="space-y-1.5 w-40">
                  <Label className="text-xs">From Date</Label>
                  <input
                    type="date"
                    value={userFromDate}
                    onChange={(e) => setUserFromDate(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1.5"
                  />
                </div>

                <div className="space-y-1.5 w-40">
                  <Label className="text-xs">To Date</Label>
                  <input
                    type="date"
                    value={userToDate}
                    onChange={(e) => setUserToDate(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1.5"
                  />
                </div>

                <Button
                  type="button"
                  onClick={fetchUserWorkReport}
                  disabled={loadingUserReport}
                  className="h-10 px-4"
                >
                  {loadingUserReport ? "Generating..." : "Generate report"}
                </Button>
              </div>

              {userWorkReport && (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="p-4 bg-muted/10 border flex flex-col justify-center">
                      <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Total Estimated Hours</span>
                      <span className="text-2xl font-bold font-heading mt-1">{userWorkReport.total_estimated_hours}h</span>
                    </Card>
                    <Card className="p-4 bg-muted/10 border flex flex-col justify-center">
                      <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Actual Hours in Range</span>
                      <span className="text-2xl font-bold font-heading mt-1 text-primary">{userWorkReport.total_actual_hours_in_range}h</span>
                    </Card>
                  </div>

                  {/* Detailed Tasks Table */}
                  <Card className="p-5 space-y-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Tasks Details ({userWorkReport.tasks.length} tasks)</div>
                    <div className="border border-border rounded-lg overflow-hidden">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead className="bg-muted/40 font-bold border-b border-border text-muted-foreground">
                          <tr>
                            <th className="p-3">Task Title</th>
                            <th className="p-3">Project</th>
                            <th className="p-3">Team</th>
                            <th className="p-3">Status</th>
                            <th className="p-3">Priority</th>
                            <th className="p-3 text-right">Est. Hours</th>
                            <th className="p-3 text-right">Hours in Range</th>
                            <th className="p-3 text-right">Total Hours</th>
                            <th className="p-3 text-right">Age</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userWorkReport.tasks.map((t, idx) => (
                            <tr key={idx} className="border-b border-border hover:bg-muted/20 last:border-0">
                              <td className="p-3 font-semibold text-primary">{t.title}</td>
                              <td className="p-3 text-muted-foreground">{t.project_name}</td>
                              <td className="p-3 text-muted-foreground">{t.team_name}</td>
                              <td className="p-3">
                                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium border border-border uppercase bg-muted/40">
                                  {TASK_STATUS_LABELS[t.status] || t.status}
                                </span>
                              </td>
                              <td className="p-3 uppercase font-medium">{t.priority}</td>
                              <td className="p-3 text-right font-mono">{t.estimated_hours}h</td>
                              <td className="p-3 text-right font-mono text-primary font-semibold">{t.actual_hours_in_range}h</td>
                              <td className="p-3 text-right font-mono">{t.total_hours}h</td>
                              <td className="p-3 text-right font-mono text-muted-foreground">{t.pending_days > 0 ? `${t.pending_days}d` : "0d"}</td>
                            </tr>
                          ))}
                          {userWorkReport.tasks.length === 0 && (
                            <tr>
                              <td colSpan={9} className="p-6 text-center text-muted-foreground italic">No tasks found matching the filter criteria.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Card>

                  {/* User Activities Feed */}
                  <Card className="p-5 space-y-3">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">User Activity Feed (Date Range)</div>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                      {userWorkReport.activities.map((a, idx) => (
                        <div key={idx} className="p-2.5 rounded border border-border flex items-center justify-between text-xs bg-muted/10 hover:bg-muted/20">
                          <span className="font-medium">{a.description}</span>
                          <span className="text-[10px] text-muted-foreground font-mono">{new Date(a.created_at).toLocaleString()}</span>
                        </div>
                      ))}
                      {userWorkReport.activities.length === 0 && (
                        <div className="text-center py-6 text-xs text-muted-foreground italic">No logged activity in this date range.</div>
                      )}
                    </div>
                  </Card>
                </div>
              )}
            </Card>
          </TabsContent>
          </>
        )}
      </Tabs>

      {/* Read-Only History Dialog Popup */}
      {selectedHistoryReport && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6 space-y-4">
            <div className="flex justify-between items-start border-b border-border pb-3">
              <div>
                <h3 className="text-lg font-bold text-primary">Standup Report details</h3>
                <div className="text-xs text-muted-foreground font-mono mt-0.5">
                  Date: {selectedHistoryReport.date} | Team: {selectedHistoryReport.team_name || "Individual"}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setSelectedHistoryReport(null)}>✕</Button>
            </div>

            <div className="space-y-4 text-sm">
              <div>
                <span className="font-semibold text-primary block">Completed Work:</span>
                <ul className="list-disc pl-5 mt-1 space-y-1 text-xs">
                  {selectedHistoryReport.completed_tasks.map((t, i) => (
                    <li key={i}>{t.title} (Est: {t.estimated_hours}h, Spent: {t.actual_hours}h)</li>
                  ))}
                  {selectedHistoryReport.completed_additional.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                  {selectedHistoryReport.completed_tasks.length === 0 && selectedHistoryReport.completed_additional.length === 0 && (
                    <span className="text-muted-foreground italic">None</span>
                  )}
                </ul>
              </div>

              <div>
                <span className="font-semibold text-primary block">Planned Work:</span>
                <ul className="list-disc pl-5 mt-1 space-y-1 text-xs">
                  {selectedHistoryReport.planned_tasks.map((t, i) => (
                    <li key={i}>
                      {t.title} (Est: {t.estimated_hours}h{t.pending_days > 0 ? `, ${t.pending_days}d pending` : ""})
                    </li>
                  ))}
                  {selectedHistoryReport.planned_additional.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                  {selectedHistoryReport.planned_tasks.length === 0 && selectedHistoryReport.planned_additional.length === 0 && (
                    <span className="text-muted-foreground italic">None</span>
                  )}
                </ul>
              </div>

              <div>
                <span className="font-semibold text-primary block">Backlog Tasks:</span>
                <ul className="list-disc pl-5 mt-1 space-y-1 text-xs">
                  {selectedHistoryReport.backlog_tasks?.map((t, i) => (
                    <li key={i}>
                      {t.title} (Est: {t.estimated_hours}h{t.pending_days > 0 ? `, ${t.pending_days}d pending` : ""})
                    </li>
                  ))}
                  {(!selectedHistoryReport.backlog_tasks || selectedHistoryReport.backlog_tasks.length === 0) && (
                    <span className="text-muted-foreground italic">None</span>
                  )}
                </ul>
              </div>

              {selectedHistoryReport.blockers && (
                <div>
                  <span className="font-semibold text-rose-600 block">Blockers:</span>
                  <div className="mt-1 bg-rose-50/50 p-2.5 rounded border border-rose-100 text-xs text-rose-800">{selectedHistoryReport.blockers}</div>
                </div>
              )}

              {selectedHistoryReport.escalations && (
                <div>
                  <span className="font-semibold text-rose-700 block">Escalations:</span>
                  <div className="mt-1 bg-rose-50/50 p-2.5 rounded border border-rose-100 text-xs text-rose-800">{selectedHistoryReport.escalations}</div>
                </div>
              )}

              {selectedHistoryReport.notes && (
                <div>
                  <span className="font-semibold text-slate-700 block">Additional Notes:</span>
                  <p className="mt-1 text-xs text-muted-foreground bg-muted/30 p-2 rounded">{selectedHistoryReport.notes}</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
